import type { Guide } from '../types';

/**
 * S-26.10-14 — awizacje: kolejka, kalendarz telefonów, wykonanie, reguła klienta (scenariusz e2e 34; prace 26.10.2026,
 * pakiet P1). Rekordy: zlecenia DEMO/2026/10/AW1–AW3 z `seed-e2e.ts`, numery awizacji z `scenario-state.mjs` (`p1`).
 */
export const guide: Guide = {
  flagKey: 'clientAvizations',
  title: 'Awizacje: kolejka, kalendarz telefonów, wykonanie, reguła klienta',
  whatItDoes:
    'Awizacja jest zadaniem przy zleceniu: punkt, kanał (telefon, mail, platforma klienta, plik Excel), termin i kontakt. Zadanie zakłada CS przy zleceniu albo powstaje samo z reguły awizacji klienta wpisanej na karcie kontrahenta — bez reguły niczego nie zgadujemy. Kolejka awizacji i kalendarz telefonów pokazują zadania według terminu, a po terminie przypominają. Przy telefonie zapisujesz wynik rozmowy, mail kopiujesz albo otwierasz w swojej poczcie i oznaczasz „Wysłane ręcznie”, pola platformy kopiujesz i oznaczasz „Wprowadzone”. Po zmianie pojazdu, terminu albo miejsca powstaje ponowna awizacja. Z aplikacji nic nie jest wysyłane.',
  audience: ['CS', 'planista FTL (podgląd)', 'Trade (podgląd)'],
  presentationItems: ['S-26.10-14'],
  steps: [
    {
      route: '/client-avizations',
      target: 'cs-awizacje-wiersz-*',
      mockupTestId: 'cs-awizacje-wiersz-{id}',
      title: 'Kolejka awizacji',
      description:
        'Awizacje do wykonania według terminu, z kanałem, źródłem (reguła klienta, założona przez CS, ponowna po zmianie) i stanem. Po terminie stan zmienia się na „po terminie — przypomnienie”.',
      action: 'Otwórz „Kolejka awizacji” w menu i kliknij numer zlecenia przy awizacji.',
      recorder: { account: 'cs' },
    },
    {
      route: '/client-avizations/:avizationId/show',
      record: { description: 'awizacja telefoniczna z kolejki', listPath: '/client-avizations' },
      target: 'aviz-phone',
      title: 'Telefon: wynik rozmowy',
      description:
        'Rozmówca, numer i tekst do przekazania z przyciskiem „Kopiuj”. Po rozmowie wybierasz wynik i dopisujesz notatkę; „brak kontaktu — ponowić” zostawia awizację do wykonania i liczy próbę.',
      action: 'Wybierz wynik rozmowy, dopisz notatkę i kliknij „Zapisz wynik rozmowy”.',
      recorder: {
        account: 'cs',
        demo: { kind: 'scenario', key: 'p1.repeatAvizationId' },
        actions: [
          { kind: 'select', label: 'Wynik rozmowy', option: 'potwierdzono termin' },
          { kind: 'fill', target: 'cs-awizacja-notatka', value: 'Termin potwierdzony z magazynem.' },
        ],
      },
    },
    {
      route: '/client-avizations/:avizationId/show',
      record: { description: 'awizacja mailowa z kolejki', listPath: '/client-avizations' },
      target: 'aviz-email',
      title: 'Mail: szkic i „Wysłane ręcznie”',
      description:
        'Szkic wiadomości z numerem zlecenia, punktem, terminem i pojazdem — czego nie wiemy, tego w treści nie ma. Kopiujesz go albo otwierasz w swojej poczcie, wysyłasz i oznaczasz „Wysłane ręcznie”.',
      action: 'Popraw treść, kliknij „Otwórz w poczcie”, wyślij i oznacz „Wysłane ręcznie”.',
      recorder: { account: 'cs', demo: { kind: 'scenario', key: 'p1.emailAvizationId' } },
    },
    {
      route: '/client-avizations/:avizationId/show',
      record: { description: 'awizacja na platformie klienta', listPath: '/client-avizations' },
      target: 'aviz-platform',
      title: 'Platforma klienta: pola do skopiowania',
      description:
        'Pola do przepisania w platformie albo pliku Excel klienta, każde z „Kopiuj”; puste pole to brak danych. Ta awizacja zastąpiła niewykonaną po zmianie pojazdu.',
      action: 'Skopiuj pola do platformy klienta i kliknij „Oznacz jako wprowadzone”.',
      recorder: { account: 'cs', demo: { kind: 'scenario', key: 'p1.platformAvizationId' } },
    },
    {
      route: '/avization-calendar',
      target: 'aviz-calendar',
      title: 'Kalendarz telefonów',
      description:
        'Awizacje telefoniczne dnia według godziny, z rozmówcą i punktem; „zadzwoń →” otwiera awizację. Widok pomocniczy — przebieg awizacji telefonicznych do ustalenia z działem.',
      action: 'Otwórz „Kalendarz telefonów” w menu i wybierz dzień.',
      recorder: { account: 'cs' },
    },
    {
      route: '/user-offers/:orderId/show',
      record: { description: 'dowolne zlecenie', listPath: '/user-offers', listFilter: { forklift: 'hide' } },
      target: 'order-avizations',
      title: 'Awizacje przy zleceniu',
      description:
        'W sekcji 7 okna zlecenia widać awizacje zlecenia z wynikiem — także planista i Trade. CS dodaje tu awizację, gdy klient nie ma reguły.',
      action: 'Rozwiń sekcję 7 „Komunikacja z klientem” i kliknij „Dodaj awizację”.',
      recorder: {
        account: 'cs',
        demo: { kind: 'order', number: 'DEMO/2026/10/AW3' },
        actions: [
          { kind: 'expand', target: 'order-section-communication' },
          { kind: 'click', target: 'order-avization-add' },
        ],
      },
    },
    {
      route: '/contractors/:contractorId/show',
      record: { description: 'kontrahent z regułą awizacji', listPath: '/contractors' },
      target: 'client-rule-avization',
      title: 'Reguła awizacji klienta',
      description:
        'Kanał, punkty, ile godzin przed oknem i kontakt — z właścicielem i datą obowiązywania, każda zmiana z powodem w historii. Reguła zakłada zadania sama dla zleceń z oknem w ciągu tygodnia.',
      action: 'Na karcie kontrahenta kliknij „Zmień regułę” w sekcji „Reguła awizacji klienta (CS)”.',
      recorder: { account: 'cs', demo: { kind: 'contractorOfOrder', number: 'DEMO/2026/10/AW1' } },
    },
  ],
};
